import React, { useEffect, useState } from "react";
import { ukDayTime } from "@/lib/time";
import { api } from "@/lib/api";
import { Loading } from "@/components/hub/Shell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PurgeButton } from "@/components/hub/PurgeButton";
import {
  Loader2, Inbox, Mail, Phone, Building2, Download, MessageSquare, Check, Clock, Archive,
  UserPlus, BadgeCheck, ArrowUpRight, Copy,
} from "lucide-react";

const KIND = {
  introducer: { label: "Introducer", tone: "border-blue-200 bg-blue-50 text-blue-700" },
  lender: { label: "Lender", tone: "border-violet-200 bg-violet-50 text-violet-700" },
  call_centre: { label: "Call centre", tone: "border-teal-200 bg-teal-50 text-teal-700" },
  media: { label: "Media / affiliate", tone: "border-[#7da9ff]/45 bg-[#7da9ff]/[0.12] text-[#3B63A8]" },
  unsure: { label: "Not sure yet", tone: "border-slate-200 bg-slate-100 text-slate-600" },
};

const FILTERS = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

const LANES = [
  { key: "all", label: "Every lane" },
  { key: "network", label: "Introducers & lenders" },
  { key: "campaign", label: "Call centres & media" },
];

const fmt = (iso) => ukDayTime(iso, "—");

export default function HubEnquiries() {
  const [status, setStatus] = useState("new");
  const [lane, setLane] = useState("all");
  const [data, setData] = useState({ items: [], decks: [], new_count: 0, campaign_new_count: 0, total: 0, deck_total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hub/network-enquiries?status=${status}&lane=${lane}&limit=100`);
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status, lane]); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="hub-enquiries">
      <div>
        <div className="eyebrow">Lending network</div>
        <h1 className="t">Enquiries from the public pages</h1>
        <p className="mt-1 text-[13.5px] text-slate-500 max-w-2xl">
          Everyone who used <b>Talk to the desk</b> or downloaded the network deck on{" "}
          <a href="/lending-network" target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">connex.is/lending-network</a>,
          and every call centre or media company that came through{" "}
          <a href="/partner-with-us" target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">connex.is/partner-with-us</a>.
          These are warm conversations, not applications — approved partners still come through Applications.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setStatus(f.key)}
                  className={`inline-flex items-center gap-1.5 rounded-[10px] border px-3 h-8 text-[12.5px] font-semibold transition-colors ${
                    status === f.key ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  data-testid={`enquiry-filter-${f.key}`}>
            {f.label}
            {f.key === "new" && data.new_count > 0 && (
              <span className={`inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] font-bold ${
                status === "new" ? "bg-white text-slate-900" : "bg-[#0055FF] text-white"}`}>{data.new_count}</span>
            )}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />
        {LANES.map((l) => (
          <button key={l.key} type="button" onClick={() => setLane(l.key)}
                  className={`inline-flex items-center gap-1.5 rounded-[10px] border px-3 h-8 text-[12.5px] font-semibold transition-colors ${
                    lane === l.key ? "bg-[#0055FF] border-[#0055FF] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  data-testid={`enquiry-lane-${l.key}`}>
            {l.label}
            {l.key === "campaign" && data.campaign_new_count > 0 && (
              <span className={`inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] font-bold ${
                lane === "campaign" ? "bg-white text-[#0055FF]" : "bg-teal-600 text-white"}`} data-testid="enquiry-lane-campaign-count">{data.campaign_new_count}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white text-center py-16 px-6" data-testid="enquiries-empty">
          <Inbox className="size-6 text-slate-400 mx-auto" />
          <div className="mt-3 text-[15px] font-semibold text-slate-900">
            {status === "new" ? "Nothing new — you're up to date" : `No ${status === "all" ? "" : status} enquiries`}
          </div>
          <p className="mt-1 text-[13px] text-slate-500 max-w-md mx-auto">
            Enquiries from the Lending Network page land here, and you're emailed the moment one arrives.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((e) => <EnquiryCard key={e.id} enquiry={e} onDone={load} />)}
        </div>
      )}

      {data.decks?.length > 0 && (
        <div className="card overflow-hidden" data-testid="deck-requests">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Download className="size-4 text-slate-400" />
              <span className="text-[13.5px] font-semibold text-slate-900">Deck downloads</span>
            </div>
            <span className="text-[12px] text-slate-500">{data.deck_total} total</span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.decks.map((d) => (
              <div key={d.id} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`deck-row-${d.id}`}>
                <span className={`inline-flex items-center rounded-full border px-2 h-5 text-[10px] font-bold uppercase tracking-[0.1em] ${(KIND[d.kind] || KIND.unsure).tone}`}>
                  {(KIND[d.kind] || KIND.unsure).label}
                </span>
                <span className="text-[13.5px] font-medium text-slate-900">{d.company || d.name || "—"}</span>
                <a href={`mailto:${d.email}`} className="text-[13px] text-[#0055FF] hover:underline">{d.email}</a>
                <span className="ml-auto text-[12px] text-slate-500">
                  {d.downloads > 0 ? `Opened ${d.downloads}×` : "Not opened yet"} · {fmt(d.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EnquiryCard({ enquiry: e, onDone }) {
  const [busy, setBusy] = useState(false);
  const [noting, setNoting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [note, setNote] = useState(e.note || "");
  const kind = KIND[e.kind] || KIND.unsure;

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await api.patch(`/hub/network-enquiries/${e.id}`, body);
      toast.success(msg);
      setNoting(false);
      window.dispatchEvent(new Event("network-enquiries:changed"));
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-5" data-testid={`enquiry-${e.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full border px-2 h-5 text-[10px] font-bold uppercase tracking-[0.1em] ${kind.tone}`}>
              {kind.label}
            </span>
            {e.status === "new" && (
              <span className="inline-flex items-center rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8] px-2 h-5 text-[10px] font-bold uppercase tracking-[0.1em]"
                    data-testid={`enquiry-new-${e.id}`}>New</span>
            )}
            {e.status === "contacted" && (
              <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-2 h-5 text-[10px] font-bold uppercase tracking-[0.1em]">Contacted</span>
            )}
            {e.status === "closed" && (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 text-slate-500 px-2 h-5 text-[10px] font-bold uppercase tracking-[0.1em]">Closed</span>
            )}
            <span className="text-[11.5px] text-slate-400 inline-flex items-center gap-1">
              <Clock className="size-3" /> {fmt(e.created_at)}
            </span>
            {e.invited_party_id && (
              <a href={e.invited_kind === "lender" ? "/hub/lenders" : "/hub/referrers"}
                 className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-2 h-5 text-[10px] font-bold uppercase tracking-[0.1em] hover:border-teal-300"
                 data-testid={`enquiry-invited-${e.id}`}>
                <BadgeCheck className="size-3" /> Invited as {e.invited_kind === "lender" ? "lender" : "introducer"}
                <ArrowUpRight className="size-3" />
              </a>
            )}
          </div>

          <div className="mt-2 text-[15px] font-semibold text-slate-900">{e.name}</div>
          <div className="mt-0.5 flex items-center gap-3 flex-wrap text-[12.5px] text-slate-500">
            {e.company && <span className="inline-flex items-center gap-1"><Building2 className="size-3.5" /> {e.company}</span>}
            <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 text-[#0055FF] hover:underline">
              <Mail className="size-3.5" /> {e.email}
            </a>
            {e.phone && (
              <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-slate-700">
                <Phone className="size-3.5" /> {e.phone}
              </a>
            )}
            {e.website && (
              <a href={/^https?:/.test(e.website) ? e.website : `https://${e.website}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 hover:text-slate-700" data-testid={`enquiry-website-${e.id}`}>
                <ArrowUpRight className="size-3.5" /> {e.website.replace(/^https?:\/\//, "")}
              </a>
            )}
            {e.scale && <span data-testid={`enquiry-scale-${e.id}`}>{e.scale}</span>}
            {Array.isArray(e.channels) && e.channels.length > 0 && (
              <span data-testid={`enquiry-channels-${e.id}`}>{e.channels.join(" · ")}</span>
            )}
            {e.source === "partner-with-us" && <span className="text-slate-400">via Partner with us</span>}
            {e.referred_by_name && (
              <span className="inline-flex items-center rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8] px-2 h-5 text-[10.5px] font-semibold" data-testid={`enquiry-referred-${e.id}`}>
                Introduced by {e.referred_by_name}
              </span>
            )}
          </div>

          {e.message && (
            <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3.5 text-[13.5px] text-slate-700 whitespace-pre-wrap leading-relaxed">
              {e.message}
            </div>
          )}
          {e.note && !noting && (
            <div className="mt-2 text-[12.5px] text-slate-500">
              <b className="text-slate-700">Your note:</b> {e.note}
            </div>
          )}
        </div>
      </div>

      {noting ? (
        <div className="mt-4">
          <Textarea value={note} onChange={(ev) => setNote(ev.target.value)} className="min-h-[80px]"
                    placeholder="What did you agree? Anything to remember before the next call?"
                    data-testid={`enquiry-note-input-${e.id}`} />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setNoting(false)}>Cancel</Button>
            <Button size="sm" disabled={busy} className="rounded-full bg-slate-900 hover:bg-slate-800"
                    onClick={() => patch({ note }, "Note saved")} data-testid={`enquiry-note-save-${e.id}`}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : "Save note"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button size="sm" asChild className="h-8 rounded-[10px] bg-[#0055FF] hover:bg-[#0044CC] text-[12px] font-semibold">
            <a href={`mailto:${e.email}?subject=Connexis%20—%20your%20enquiry`} data-testid={`enquiry-reply-${e.id}`}>
              <MessageSquare className="size-3.5 mr-1.5" /> Reply
            </a>
          </Button>
          {!e.invited_party_id && (
            <Button size="sm" variant="outline" className="h-8 rounded-[10px] text-[12px] font-semibold border-teal-200 text-teal-700 hover:border-teal-300 hover:bg-teal-50"
                    onClick={() => setInviting(true)} data-testid={`enquiry-invite-${e.id}`}>
              <UserPlus className="size-3.5 mr-1.5" /> Invite as partner
            </Button>
          )}
          {e.status !== "contacted" && (
            <Button size="sm" variant="outline" disabled={busy} className="h-8 rounded-[10px] text-[12px]"
                    onClick={() => patch({ status: "contacted" }, "Marked as contacted")}
                    data-testid={`enquiry-contacted-${e.id}`}>
              <Check className="size-3.5 mr-1.5" /> Mark contacted
            </Button>
          )}
          {e.status !== "closed" && (
            <Button size="sm" variant="outline" disabled={busy} className="h-8 rounded-[10px] text-[12px] text-slate-500"
                    onClick={() => patch({ status: "closed" }, "Closed")} data-testid={`enquiry-close-${e.id}`}>
              <Archive className="size-3.5 mr-1.5" /> Close
            </Button>
          )}
          <PurgeButton kind="enquiry" ids={[e.id]} onDone={onDone} testId={`enquiry-purge-${e.id}`} />
          <Button size="sm" variant="ghost" className="h-8 rounded-[10px] text-[12px] text-slate-500"
                  onClick={() => setNoting(true)} data-testid={`enquiry-note-${e.id}`}>
            {e.note ? "Edit note" : "Add a note"}
          </Button>
        </div>
      )}

      {inviting && (
        <InviteDialog enquiry={e} onClose={() => setInviting(false)} onDone={onDone} />
      )}
    </div>
  );
}

const MODELS = {
  introducer: [
    { key: "revenue_share", label: "% of funded amount", unit: "%", value: 1.5, field: "commission_pct" },
    { key: "commission_share", label: "% of our commission", unit: "%", value: 50, field: "commission_pct" },
    { key: "flat_funded", label: "£ per funded deal", unit: "£", value: 500, field: "commission_flat_gbp" },
    { key: "flat_lead", label: "£ per qualified lead", unit: "£", value: 25, field: "commission_flat_gbp" },
  ],
  lender: [
    { key: "percentage", label: "% of funded value", unit: "%", value: 2, field: "commission_pct" },
    { key: "flat", label: "£ per funded deal", unit: "£", value: 1000, field: "commission_flat_gbp" },
  ],
};

function InviteDialog({ enquiry: e, onClose, onDone }) {
  const [kind, setKind] = useState(e.kind === "lender" ? "lender" : "introducer");
  const [model, setModel] = useState(MODELS[e.kind === "lender" ? "lender" : "introducer"][0]);
  const [amount, setAmount] = useState(MODELS[e.kind === "lender" ? "lender" : "introducer"][0].value);
  const [form, setForm] = useState({ name: e.name || "", company: e.company || "", email: e.email || "", phone: e.phone || "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const pickKind = (k) => {
    setKind(k);
    setModel(MODELS[k][0]);
    setAmount(MODELS[k][0].value);
  };
  const pickModel = (m) => { setModel(m); setAmount(m.value); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // refresh the list only on close — refreshing mid-flow unmounts this card (and the dialog)
  const finish = () => { onDone?.(); onClose(); };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/hub/network-enquiries/${e.id}/invite`, {
        kind, name: form.name.trim(), company: form.company.trim() || null,
        email: form.email.trim(), phone: form.phone.trim() || null,
        commission_type: model.key,
        commission_pct: model.field === "commission_pct" ? Number(amount) || 0 : 0,
        commission_flat_gbp: model.field === "commission_flat_gbp" ? Number(amount) || 0 : 0,
      });
      setDone(r.data);
      toast.success(r.data.invite_emailed
        ? `Invited — set-password link sent to ${form.email.trim()}`
        : "Partner created — the email didn't send, copy the link below instead");
      window.dispatchEvent(new Event("network-enquiries:changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && (done ? finish() : onClose())}>
      <DialogContent className="max-w-lg" data-testid="enquiry-invite-dialog">
        <DialogHeader>
          <DialogTitle className="text-[19px] tracking-[-0.01em]">
            {done ? "They're invited" : "Invite them as a partner"}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            {done
              ? "Their portal opens the moment they set a password. Terms can be changed any time."
              : "This creates the partner record and emails plus texts them a link to set their own password."}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 text-[13.5px] text-teal-800">
              <b>{form.company || form.name}</b> is now {done.kind === "lender" ? "a lender" : "an introducer"} in the Hub
              {done.invite_emailed ? " and has been sent their set-up link." : ". The email didn't go out — send them this link:"}
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={done.invite_url} className="text-[12px] font-mono" data-testid="invite-url" />
              <Button variant="outline" size="icon" className="shrink-0"
                      onClick={() => { navigator.clipboard.writeText(done.invite_url); toast.success("Link copied"); }}
                      data-testid="invite-copy">
                <Copy className="size-4" />
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" asChild>
                <a href={done.kind === "lender" ? "/hub/lenders" : "/hub/referrers"} data-testid="invite-open-partner">
                  Open their profile <ArrowUpRight className="size-3.5 ml-1.5" />
                </a>
              </Button>
              <Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={finish} data-testid="invite-close">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {e.referred_by_name && (
              <div className="rounded-[10px] border border-[#7da9ff]/45 bg-[#7da9ff]/[0.12] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-[#3B63A8]"
                   data-testid="invite-recruiter-line">
                Recruited by <b className="text-slate-900">{e.referred_by_name}</b>
                {e.referred_by_override_pct != null && <> — they earn a <b className="text-slate-900">{e.referred_by_override_pct}% override</b> on every deal this partner produces</>}.
                Paid by Connexis, never taken out of the partner's own commission. This attaches automatically when you invite.
              </div>
            )}
            <div>
              <Label>Joining as</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {["introducer", "lender"].map((k) => (
                  <button key={k} type="button" onClick={() => pickKind(k)} data-testid={`invite-kind-${k}`}
                          className={`h-10 rounded-xl border text-[13px] font-semibold capitalize transition-colors ${
                            kind === k ? "border-[#0055FF] bg-[#0055FF]/[0.06] text-[#0055FF]"
                                       : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact name</Label>
                <Input value={form.name} onChange={(ev) => set("name", ev.target.value)} className="mt-1.5" data-testid="invite-name" /></div>
              <div><Label>{kind === "lender" ? "Company (required)" : "Company"}</Label>
                <Input value={form.company} onChange={(ev) => set("company", ev.target.value)} className="mt-1.5" data-testid="invite-company" /></div>
              <div><Label>Email</Label>
                <Input value={form.email} onChange={(ev) => set("email", ev.target.value)} className="mt-1.5" data-testid="invite-email" /></div>
              <div><Label>Mobile (for the text)</Label>
                <Input value={form.phone} onChange={(ev) => set("phone", ev.target.value)} className="mt-1.5" placeholder="Optional" data-testid="invite-phone" /></div>
            </div>

            <div>
              <Label>{kind === "lender" ? "Connexis fee" : "Commission model"}</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {MODELS[kind].map((m) => (
                  <button key={m.key} type="button" onClick={() => pickModel(m)} data-testid={`invite-model-${m.key}`}
                          className={`inline-flex items-center rounded-[10px] border px-3 h-8 text-[12px] font-semibold transition-colors ${
                            model.key === m.key ? "border-[#0055FF] bg-[#0055FF]/[0.06] text-[#0055FF]"
                                                : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <span className="text-[13px] text-slate-500 w-4">{model.unit}</span>
                <Input type="number" step="0.1" min="0" value={amount} onChange={(ev) => setAmount(ev.target.value)}
                       className="w-32" data-testid="invite-amount" />
                <span className="text-[12.5px] text-slate-500">{model.label}</span>
              </div>
              {model.unit === "%" && Number(amount) > 25 && (
                <p className="mt-2 text-[11.5px] text-[#3B63A8]" data-testid="invite-amount-warn">
                  Unusual rate — {amount}% is well above a typical commission. Worth a second look.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
              <Button disabled={busy || !form.name.trim() || !form.email.trim() || (kind === "lender" && !form.company.trim())}
                      onClick={submit}
                      className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] font-semibold" data-testid="invite-submit">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <><UserPlus className="size-3.5 mr-1.5" /> Create and send invite</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <div className="text-[10.5px] font-semibold tracking-[0.16em] uppercase text-slate-500">{children}</div>;
}
